import React, {createContext, use, useMemo} from "react";
import PropTypes from "prop-types";
import {usePermissions} from "./AuthzProvider";
import {useOrgSettings} from "./OrgSettingsProvider";

const HomeContext = createContext();

const HomeProvider = ({children}) => {
  const {aiPermissions, showRegulatoryResearch} = useOrgSettings();
  const {
    canReadDashboard,
    canAccessExerciseOrIncident,
    canAccessAuditSummary,
    canReadResponsePlan,
    canAccessDocumentLibrary,
    canReadRiskAssessment,
    canAccessTrustedAdvisor,
    canAccessMessage,
    canReadNews,
    canManageTask,
    canManageObligation,
    canManagePlaybook,
    canManageReportTemplate,
    canManageDataset,
    canManageDataOption,
    canManageUser,
    canManageRole,
    canManagePermission,
    canManageOrganization,
    canManageNotification,
    canManageIntegration,
    canManageApiKey,
  } = usePermissions();

  let home = "/userProfile";

  if (canReadDashboard && canAccessExerciseOrIncident) {
    home = "/dashboard";
  } else if (canAccessExerciseOrIncident) {
    home = "/actions/list";
  } else if (canAccessAuditSummary) {
    home = "/compliance/summary";
  } else if (showRegulatoryResearch) {
    home = "/compliance/research";
  } else if (canReadResponsePlan) {
    home = "/compliance/plan";
  } else if (canAccessDocumentLibrary) {
    home = "/library";
  } else if (canReadRiskAssessment) {
    home = "/reports/assessment";
  } else if (canAccessTrustedAdvisor && aiPermissions?.trustedAdvisor) {
    home = "/rex-ai-maestro";
  } else if (canAccessMessage) {
    home = "/messaging/list";
  } else if (canReadNews) {
    home = "/news";
  } else if (canManageTask) {
    home = "/tasks/list";
  } else if (canManageObligation) {
    home = "/procedures/list";
  } else if (canManagePlaybook) {
    home = "/playbooks/list";
  } else if (canManageReportTemplate) {
    home = "/reportTemplates/list";
  } else if (canManageDataset) {
    home = "/datasets/list";
  } else if (canManageDataOption) {
    home = "/dataOptions/conditions/list";
  } else if (canManageUser) {
    home = "/users/list";
  } else if (canManageRole) {
    home = "/roles/list";
  } else if (canManagePermission) {
    home = "/permissions";
  } else if (canManageOrganization) {
    home = "/organization/general";
  } else if (canManageNotification) {
    home = "/notifications";
  } else if (canManageIntegration) {
    home = "/integrations";
  } else if (canManageApiKey) {
    home = "/api/list";
  }

  const value = useMemo(() => ({home}), [home]);

  return <HomeContext value={value}>{children}</HomeContext>;
};

HomeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useHome = () => {
  const context = use(HomeContext);
  if (!context) {
    throw new Error("Context must be used within the correct provider");
  }
  return context;
};

const MemoryHomeProvider = ({children}) => {
  return <HomeContext value={{home: "/dashboard"}}>{children}</HomeContext>;
};

MemoryHomeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {HomeProvider, MemoryHomeProvider, useHome};
